import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { Clinic, ClinicsFilters } from "../clinics.types";
import { clinicsConfig } from "../clinics.config";
import { localClinicsData } from "../localClinicsData";

const PAGE_SIZE = clinicsConfig.itemsPerPage;

/**
 * Apply client-side filters to a clinic list.
 * Mirrors the CMS filtering logic so toggle between "Current" and "New"
 * feels consistent.
 */
function matchesFilters(clinic: Clinic, filters: ClinicsFilters): boolean {
  // Search: match against name, city, state
  if (filters.search) {
    const q = filters.search.toLowerCase();
    const haystack = [
      clinic.name,
      clinic.address?.city,
      clinic.address?.state,
      clinic.address?.postalCode,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(q)) return false;
  }

  // Treatments (any match)
  if (filters.treatments?.length) {
    if (!filters.treatments.some((t) => clinic.treatments?.includes(t))) {
      return false;
    }
  }

  // Conditions (any match)
  if (filters.conditions?.length) {
    if (!filters.conditions.some((c) => clinic.conditions?.includes(c))) {
      return false;
    }
  }

  // Services (any match)
  if (filters.services?.length) {
    if (!filters.services.some((s) => clinic.services?.includes(s))) {
      return false;
    }
  }

  // Clinic type (exact match)
  if (filters.clinicType) {
    if (clinic.clinicType !== filters.clinicType) return false;
  }

  // Consultation types (any match)
  if (filters.consultationTypes?.length) {
    if (
      !filters.consultationTypes.some((ct) =>
        clinic.consultationTypes?.includes(ct),
      )
    ) {
      return false;
    }
  }

  // Patient groups (any match)
  if (filters.patientGroups?.length) {
    if (
      !filters.patientGroups.some((pg) => clinic.patientGroups?.includes(pg))
    ) {
      return false;
    }
  }

  // Financiers (any match)
  if (filters.financiers?.length) {
    if (!filters.financiers.some((f) => clinic.financiers?.includes(f))) {
      return false;
    }
  }

  // Accessibilities (any match)
  if (filters.accessibilities?.length) {
    if (
      !filters.accessibilities.some((a) => clinic.accessibilities?.includes(a))
    ) {
      return false;
    }
  }

  // Employee benefits (any match)
  if (filters.employeeBenefits?.length) {
    if (
      !filters.employeeBenefits.some((eb) =>
        clinic.employeeBenefits?.includes(eb),
      )
    ) {
      return false;
    }
  }

  // Insurance accepted
  if (filters.insuranceAccepted === true) {
    if (!clinic.insuranceAccepted) return false;
  }

  // Free first consultation
  if (filters.isFirstConsultationFree === true) {
    if (!clinic.isFirstConsultationFree) return false;
  }

  // Reported annual IVF data
  if (filters.hasReportedAnnualIvfData === true) {
    if (!clinic.hasReportedAnnualIvfData) return false;
  }

  return true;
}

/**
 * Hook that serves clinics from the local JSON file with client-side
 * filtering and pagination. Returns an interface compatible with
 * what Clinics.tsx expects from usePaginatedQuery.
 */
export function useLocalClinics(filters: ClinicsFilters) {
  const [pageCount, setPageCount] = useState(1);

  const allClinics = localClinicsData;

  // Apply filters and sort alphabetically
  const filtered = useMemo(
    () =>
      allClinics
        .filter((c) => matchesFilters(c, filters))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allClinics, filters],
  );

  // Reset pagination when filters change
  const filterKey = JSON.stringify(filters);
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setPageCount(1);
    }
  }, [filterKey]);

  // Paginate
  const data = useMemo(
    () => filtered.slice(0, pageCount * PAGE_SIZE),
    [filtered, pageCount],
  );

  const hasNextPage = data.length < filtered.length;

  const fetchNextPage = useCallback(() => {
    setPageCount((prev) => prev + 1);
  }, []);

  return {
    data,
    totalFiltered: filtered.length,
    isLoading: false,
    error: null,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage: false,
    isRefetching: false,
    refetch: () => {},
  };
}
